import type { ChatMessageModel } from '../client/SmartAssistantBackendClient';
import { extractContentText } from '../utils/chatMessageUtils';
import { getSequencedState } from './getSequencedState';
import { releaseSequencedBuffer } from './releaseSequencedBuffer';
import { handleSequenceGap } from './handleSequenceGap';
export const handleSequencedPayload = (params: {
  targetChatId: string;
  seqValue: number;
  content: any;
  getChatBuffer: (id: string, fallback: ChatMessageModel[]) => ChatMessageModel[];
  mergeMessages: (current: ChatMessageModel[], payload: any, eventName?: string) => ChatMessageModel[];
  setChatBuffer: (id: string, messages: ChatMessageModel[]) => void;
  activeChatIdRef: React.MutableRefObject<string | null>;
  setHistory: React.Dispatch<React.SetStateAction<ChatMessageModel[]>>;
  sequencedMessagesRef: React.MutableRefObject<Map<string, { nextSeq: number; buffer: Map<number, string>; lastSeq: number }>>;
  sequencedFlushTimersRef: React.MutableRefObject<Map<string, number>>;
  lastReceivedSeqRef: React.MutableRefObject<Map<string, number>>;
  missingSeqRef: React.MutableRefObject<Map<string, number>>;
  missingSeqRetriesRef: React.MutableRefObject<Map<string, number>>;
  missingSeqTimersRef: React.MutableRefObject<Map<string, number>>;
  requestReplay: (targetChatId: string, lastSeq: number) => void;
  flushSequencedBuffer: (targetChatId: string, state: { nextSeq: number; buffer: Map<number, string>; lastSeq: number }) => void;
}) => {
  const {
    targetChatId,
    seqValue,
    getChatBuffer,
    mergeMessages,
    setChatBuffer,
    activeChatIdRef,
    setHistory,
    sequencedMessagesRef,
    sequencedFlushTimersRef,
    lastReceivedSeqRef,
    missingSeqRef,
    missingSeqRetriesRef,
    missingSeqTimersRef,
    requestReplay,
    flushSequencedBuffer,
  } = params;
  const { state, previousReceived } = getSequencedState({
    targetChatId,
    seqValue,
    sequencedMessagesRef,
    lastReceivedSeqRef,
    missingSeqRef,
    missingSeqRetriesRef,
    missingSeqTimersRef,
  });
  if (seqValue < state.nextSeq) {
    return true;
  }
  const expectedSeq = previousReceived + 1;
  const gapDetected = previousReceived > 0 && seqValue !== expectedSeq;
  state.buffer.set(seqValue, extractContentText(params.content ?? ''));
  let merged = getChatBuffer(targetChatId, []);
  const releaseResult = releaseSequencedBuffer({ state, merged, mergeMessages });
  merged = releaseResult.merged;
  const released = releaseResult.released;
  setChatBuffer(targetChatId, [...merged]);
  handleSequenceGap({
    targetChatId,
    expectedSeq,
    seqValue,
    gapDetected,
    missingSeqRef,
    missingSeqRetriesRef,
    missingSeqTimersRef,
    sequencedMessagesRef,
    requestReplay,
    flushSequencedBuffer,
  });
  if (state.buffer.size === 0) {
    const timerId = sequencedFlushTimersRef.current.get(targetChatId);
    if (timerId) {
      clearTimeout(timerId);
      sequencedFlushTimersRef.current.delete(targetChatId);
    }
  }
  if (!released) {
    return true;
  }
  if (activeChatIdRef.current === targetChatId) {
    setHistory(merged);
  }
  return true;
};
